import { useCallback } from "react";
import { ResourceType, OutlookRecipient, OutlookLocation } from "../types/resource";
import { usePlatform, WebAddinPlatforms } from "./usePlatform";
import { useOutlookStore } from "../store/outlookStore";

declare const Office: any;

// Enum for Exchange Resource Types (matching the original logic)
export enum ExchangeResourceTypeEnum {
  Room = 0,
  Equipment = 1,
  Workspace = 2,
}

// Helper function to validate Office.js availability
const validateOfficeContext = (): boolean => {
  return !!(Office && Office.context && Office.context.mailbox && Office.context.mailbox.item);
};

export const useOutlookActions = () => {
  const { getCurrentPlatform, isSetSupported } = usePlatform();
  const {
    setAppointmentTime,
    setSubject,
    setLocation,
    setEnhancedLocation,
    setOrganizer,
    setRequiredAttendees,
    setOptionalAttendees,
    setTimeZone,
    setError,
  } = useOutlookStore();

  // Convert resources to EmailUser format for required attendees
  const convertToEmailUser = useCallback((resources: ResourceType[]): Office.EmailUser[] => {
    return resources.map((resource) => ({
      displayName: resource.Name,
      emailAddress: resource.Email,
    }));
  }, []);

  // Convert resources to LocationIdentifier format for enhanced location
  const convertToLocationIdentifier = useCallback((resources: ResourceType[]): Office.LocationIdentifier[] => {
    return (
      resources
        // .filter(
        //   (resource) =>
        //     resource.ResourceType === ExchangeResourceTypeEnum.Room ||
        //     resource.ResourceType === ExchangeResourceTypeEnum.Workspace
        // )
        .map((resource) => ({
          id: resource.Email,
          type: Office.MailboxEnums.LocationType.Room,
        }))
    );
  }, []);

  // Convert equipment resources to EmailUser format
  const convertEquipmentToEmailUser = useCallback((resources: ResourceType[]): Office.EmailUser[] => {
    return (
      resources
        // .filter((resource) => resource.ResourceType === ExchangeResourceTypeEnum.Equipment)
        .map((resource) => ({
          displayName: resource.Name,
          emailAddress: resource.Email,
        }))
    );
  }, []);

  // Internal function to set locations
  const setLocationsInternal = useCallback(async (locations: string[]): Promise<boolean> => {
    try {
      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("Not an appointment item");
        return false;
      }

      const appCompose = item as Office.AppointmentCompose;
      const locationsString = locations.join(";");

      await new Promise<void>((resolve, reject) => {
        appCompose.location.setAsync(locationsString, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(`Failed to set location: ${result.error?.message}`));
          }
        });
      });

      return true;
    } catch (error) {
      console.error("Failed to set locations:", error);
      return false;
    }
  }, []);

  // Add resources to appointment
  const AddResources = useCallback(
    async (resources: ResourceType[], locations?: string[]): Promise<boolean> => {
      try {
        console.log("🚀 AddResources called with:", { resources: resources?.length, locations: locations?.length });

        // Validate Office.js context
        if (!validateOfficeContext()) {
          console.error("❌ Office.js context not available");
          return false;
        }

        const item = Office.context.mailbox.item;
        if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
          console.error("❌ Not an appointment item", { itemType: item?.itemType });
          return false;
        }

        const appCompose = item as Office.AppointmentCompose;
        const currentPlatform = getCurrentPlatform();
        const isMailbox18Supported = !Office.context.requirements.isSetSupported("Mailbox", "1.8");
        const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

        console.log("📋 Platform info:", { currentPlatform, isMailbox18Supported, isPlatformMac });

        if (!resources || resources.length === 0) {
          console.warn("⚠️ No resources to add");
          return true;
        }

        // For older API versions, add all resources as enhanced locations
        if (!isMailbox18Supported) {
          console.log("📤 Using legacy approach (Mailbox < 1.8) with enhancedLocation");
          const locationIdentifiers = convertToLocationIdentifier(resources);

          if (locationIdentifiers.length > 0) {
            await new Promise<void>((resolve, reject) => {
              appCompose.enhancedLocation.addAsync(locationIdentifiers, (result: Office.AsyncResult<void>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  console.log("✅ Successfully added enhanced locations:", locationIdentifiers.length);
                  resolve();
                } else {
                  console.error("❌ Failed to add enhanced locations:", result.error);
                  reject(new Error(`Failed to add enhanced locations: ${result.error?.message}`));
                }
              });
            });
          }

          // Set location for older versions
          if (locations && locations.length > 0) {
            await setLocationsInternal(locations);
          }
        } else {
          // For newer versions (Mailbox 1.8+), handle equipment and rooms separately
          console.log("📤 Using modern approach (Mailbox 1.8+)");

          // Add equipment as required attendees
          const equipmentUsers = convertEquipmentToEmailUser(resources);
          if (equipmentUsers.length > 0) {
            await new Promise<void>((resolve, reject) => {
              appCompose.requiredAttendees.addAsync(equipmentUsers, (result: Office.AsyncResult<void>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  console.log("✅ Successfully added equipment:", equipmentUsers.length);
                  resolve();
                } else {
                  console.error("❌ Failed to add equipment:", result.error);
                  reject(new Error(`Failed to add equipment: ${result.error?.message}`));
                }
              });
            });
          }

          // Add rooms/workspaces as enhanced locations (not supported on Mac)
          if (!isPlatformMac) {
            const locationIdentifiers = convertToLocationIdentifier(resources);
            if (locationIdentifiers.length > 0) {
              await new Promise<void>((resolve, reject) => {
                appCompose.enhancedLocation.addAsync(locationIdentifiers, (result: Office.AsyncResult<void>) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    console.log("✅ Successfully added enhanced locations:", locationIdentifiers.length);
                    resolve();
                  } else {
                    console.error("❌ Failed to add enhanced locations:", result.error);
                    // Don't reject for enhanced locations on unsupported platforms
                    if (result.error?.code === 5001) {
                      console.warn("⚠️ Enhanced locations not supported on this platform");
                      resolve();
                    } else {
                      reject(new Error(`Failed to add enhanced locations: ${result.error?.message}`));
                    }
                  }
                });
              });
            }
          }

          // Set basic location as fallback or for Mac
          if (locations && locations.length > 0) {
            await setLocationsInternal(locations);
          }
        }

        console.log("✅ AddResources completed successfully");
        return true;
      } catch (error) {
        console.error("❌ Failed to add resources:", error);
        return false;
      }
    },
    [
      getCurrentPlatform,
      isSetSupported,
      convertToEmailUser,
      convertToLocationIdentifier,
      convertEquipmentToEmailUser,
      setLocationsInternal,
    ]
  );

  // Remove resources from appointment
  const RemoveResources = useCallback(
    async (resources: ResourceType[]): Promise<boolean> => {
      try {
        console.log("🗑️ RemoveResources called with:", { resources: resources?.length });

        // Validate Office.js context
        if (!validateOfficeContext()) {
          console.error("❌ Office.js context not available");
          return false;
        }

        const item = Office.context.mailbox.item;
        if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
          console.error("❌ Not an appointment item", { itemType: item?.itemType });
          return false;
        }

        const appCompose = item as Office.AppointmentCompose;
        const currentPlatform = getCurrentPlatform();
        const isMailbox18Supported = !Office.context.requirements.isSetSupported("Mailbox", "1.8");
        const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

        console.log("📋 Platform info:", { currentPlatform, isMailbox18Supported, isPlatformMac });

        if (!resources || resources.length === 0) {
          console.warn("⚠️ No resources to remove");
          return true;
        }

        // Get organizer email to exclude from removal
        let organizerEmail = "";
        try {
          const organizer = await new Promise<Office.EmailAddressDetails>((resolve, reject) => {
            appCompose.organizer.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails>) => {
              if (result.status === Office.AsyncResultStatus.Succeeded && result.value) {
                resolve(result.value);
              } else {
                // Fallback to user profile
                resolve({
                  displayName: Office.context.mailbox.userProfile.displayName,
                  emailAddress: Office.context.mailbox.userProfile.emailAddress,
                } as Office.EmailAddressDetails);
              }
            });
          });
          organizerEmail = organizer.emailAddress || "";
        } catch (error) {
          console.warn("⚠️ Could not get organizer email, using user profile:", error);
          organizerEmail = Office.context.mailbox.userProfile.emailAddress || "";
        }

        // Get current required attendees
        const currentAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
          appCompose.requiredAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              console.log("📋 Current attendees:", result.value?.length);
              resolve(result.value || []);
            } else {
              console.error("❌ Failed to get current attendees:", result.error);
              reject(new Error(`Failed to get required attendees: ${result.error?.message}`));
            }
          });
        });

        // Filter out resources to remove (excluding organizer)
        const emailsToRemove = new Set(resources.map((r) => r.Email.toLowerCase()));
        const remainingAttendees = currentAttendees.filter((attendee) => {
          const email = attendee.emailAddress.toLowerCase();
          const shouldKeep = email !== organizerEmail.toLowerCase() && !emailsToRemove.has(email);
          if (!shouldKeep && email !== organizerEmail.toLowerCase()) {
            console.log("🗑️ Removing attendee:", attendee.emailAddress);
          }
          return shouldKeep;
        });

        // Convert to EmailUser format for setAsync
        const emailUsers: Office.EmailUser[] = remainingAttendees.map((attendee) => ({
          displayName: attendee.displayName,
          emailAddress: attendee.emailAddress,
        }));

        // Update required attendees
        await new Promise<void>((resolve, reject) => {
          appCompose.requiredAttendees.setAsync(emailUsers, (result: Office.AsyncResult<void>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              console.log("✅ Successfully updated attendees");
              resolve();
            } else {
              console.error("❌ Failed to update attendees:", result.error);
              reject(new Error(`Failed to update required attendees: ${result.error?.message}`));
            }
          });
        });

        // Handle enhanced location removal for all versions (except Mac for modern API)
        if (!isMailbox18Supported) {
          // Legacy API: Remove from enhanced locations (consistent with AddResources)
          console.log("📤 Using legacy removal approach (Mailbox < 1.8) with enhancedLocation");
          const locationIdentifiers = convertToLocationIdentifier(resources);
          if (locationIdentifiers.length > 0) {
            await new Promise<void>((resolve, reject) => {
              appCompose.enhancedLocation.removeAsync(locationIdentifiers, (result: Office.AsyncResult<void>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  console.log("✅ Successfully removed enhanced locations (legacy)");
                  resolve();
                } else {
                  console.error("❌ Failed to remove enhanced locations (legacy):", result.error);
                  // Don't reject, as this might not be critical
                  if (result.error?.code === 5001) {
                    console.warn("⚠️ Enhanced locations removal not supported on this platform");
                  }
                  resolve();
                }
              });
            });
          }
        } else {
          // Modern API: Remove from enhanced locations (not supported on Mac)
          if (!isPlatformMac) {
            const locationIdentifiers = convertToLocationIdentifier(resources);
            if (locationIdentifiers.length > 0) {
              await new Promise<void>((resolve, reject) => {
                appCompose.enhancedLocation.removeAsync(locationIdentifiers, (result: Office.AsyncResult<void>) => {
                  if (result.status === Office.AsyncResultStatus.Succeeded) {
                    console.log("✅ Successfully removed enhanced locations (modern)");
                    resolve();
                  } else {
                    console.error("❌ Failed to remove enhanced locations (modern):", result.error);
                    // Don't reject, as this might not be critical
                    if (result.error?.code === 5001) {
                      console.warn("⚠️ Enhanced locations removal not supported on this platform");
                    }
                    resolve();
                  }
                });
              });
            }
          }
        }

        // For Mac, clear basic location if rooms were removed
        if (isPlatformMac) {
          const roomResources = resources.filter((r) => r.RecipientType === ExchangeResourceTypeEnum.Room);
          if (roomResources.length > 0) {
            console.log("📍 Clearing basic location for removed rooms (Mac)");
            await setLocationsInternal([]); // Clear location
          }
        }

        console.log("✅ RemoveResources completed successfully");
        return true;
      } catch (error) {
        console.error("❌ Failed to remove resources:", error);
        return false;
      }
    },
    [getCurrentPlatform, isSetSupported, convertToLocationIdentifier, setLocationsInternal]
  );

  // Update recipients (both required and optional)
  const UpdateRecipients = useCallback(async (recipients: OutlookRecipient[]): Promise<boolean> => {
    try {
      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("Not an appointment item");
        return false;
      }

      const appCompose = item as Office.AppointmentCompose;
      const organizerEmail = Office.context.mailbox.userProfile.emailAddress.toLowerCase();

      // Separate required and optional recipients
      const requiredRecipients = recipients.filter((recipient) => recipient.Email.toLowerCase() !== organizerEmail);
      const optionalRecipients: OutlookRecipient[] = []; // Could be extended based on business logic

      // Convert to Office format
      const requiredEmailDetails: Office.EmailAddressDetails[] = requiredRecipients.map((recipient) => ({
        displayName: recipient.Name,
        emailAddress: recipient.Email,
        appointmentResponse: Office.MailboxEnums.ResponseType.None,
        recipientType: Office.MailboxEnums.RecipientType.Other,
      }));

      const optionalEmailUsers: Office.EmailUser[] = optionalRecipients.map((recipient) => ({
        displayName: recipient.Name,
        emailAddress: recipient.Email,
      }));

      // Update required attendees
      await new Promise<void>((resolve, reject) => {
        appCompose.requiredAttendees.setAsync(requiredEmailDetails, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(`Failed to update required attendees: ${result.error?.message}`));
          }
        });
      });

      // Update optional attendees
      await new Promise<void>((resolve, reject) => {
        appCompose.optionalAttendees.setAsync(optionalEmailUsers, (result: Office.AsyncResult<void>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(`Failed to update optional attendees: ${result.error?.message}`));
          }
        });
      });

      return true;
    } catch (error) {
      console.error("Failed to update recipients:", error);
      return false;
    }
  }, []);

  // Set locations
  const SetLocations = useCallback(
    async (locations: string[]): Promise<boolean> => {
      return setLocationsInternal(locations);
    },
    [setLocationsInternal]
  );

  // Get current recipients
  const GetRecipients = useCallback(async (): Promise<OutlookRecipient[]> => {
    try {
      console.log("📋 GetRecipients called");

      // Validate Office.js context
      if (!validateOfficeContext()) {
        console.error("❌ Office.js context not available");
        return [];
      }

      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("❌ Not an appointment item", { itemType: item?.itemType });
        return [];
      }

      const appCompose = item as Office.AppointmentCompose;
      const currentPlatform = getCurrentPlatform();
      const isMailbox18Supported = !Office.context.requirements.isSetSupported("Mailbox", "1.8");
      const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

      console.log("📋 Platform info:", { currentPlatform, isMailbox18Supported, isPlatformMac });

      // Get organizer email to exclude
      let organizerEmail = "";
      try {
        organizerEmail = Office.context.mailbox.userProfile.emailAddress || "";
      } catch (error) {
        console.warn("⚠️ Could not get organizer email:", error);
      }

      // Get required attendees
      const requiredAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
        appCompose.requiredAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log("📋 Required attendees:", result.value?.length);
            resolve(result.value || []);
          } else {
            console.error("❌ Failed to get required attendees:", result.error);
            resolve([]); // Don't reject, just return empty array
          }
        });
      });

      // Get optional attendees
      const optionalAttendees = await new Promise<Office.EmailAddressDetails[]>((resolve, reject) => {
        appCompose.optionalAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            console.log("📋 Optional attendees:", result.value?.length);
            resolve(result.value || []);
          } else {
            console.error("❌ Failed to get optional attendees:", result.error);
            resolve([]); // Don't reject, just return empty array
          }
        });
      });

      // Get enhanced locations (if supported and not Mac)
      let enhancedLocations: Office.LocationDetails[] = [];
      if (isMailbox18Supported && !isPlatformMac) {
        try {
          enhancedLocations = await new Promise<Office.LocationDetails[]>((resolve, reject) => {
            appCompose.enhancedLocation.getAsync((result: Office.AsyncResult<Office.LocationDetails[]>) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                console.log("📋 Enhanced locations:", result.value?.length);
                resolve(result.value || []);
              } else {
                console.warn("⚠️ Failed to get enhanced locations:", result.error);
                resolve([]); // Don't reject, just return empty array
              }
            });
          });
        } catch (error) {
          console.warn("⚠️ Failed to get enhanced locations:", error);
        }
      }

      // Combine all recipients, excluding organizer
      const allRecipients: OutlookRecipient[] = [];

      // Add required attendees (excluding organizer)
      requiredAttendees.forEach((attendee) => {
        if (attendee.emailAddress.toLowerCase() !== organizerEmail.toLowerCase()) {
          allRecipients.push({
            Name: attendee.displayName || attendee.emailAddress,
            Email: attendee.emailAddress,
            RecipientType: ExchangeResourceTypeEnum.Equipment, // Default to equipment for attendees
          });
        }
      });

      // Add optional attendees (excluding organizer)
      optionalAttendees.forEach((attendee) => {
        if (attendee.emailAddress.toLowerCase() !== organizerEmail.toLowerCase()) {
          allRecipients.push({
            Name: attendee.displayName || attendee.emailAddress,
            Email: attendee.emailAddress,
            RecipientType: ExchangeResourceTypeEnum.Equipment,
          });
        }
      });

      // Add enhanced locations as rooms
      enhancedLocations.forEach((location) => {
        const email = location.emailAddress || location.locationIdentifier?.id || "";
        if (email && email.toLowerCase() !== organizerEmail.toLowerCase()) {
          allRecipients.push({
            Name: location.displayName || email,
            Email: email,
            RecipientType: ExchangeResourceTypeEnum.Room,
          });
        }
      });

      console.log("✅ GetRecipients completed:", { totalRecipients: allRecipients.length });
      return allRecipients;
    } catch (error) {
      console.error("❌ Failed to get recipients:", error);
      return [];
    }
  }, [getCurrentPlatform, isSetSupported]);

  // Initialize appointment data from Outlook and sync with store
  const InitializeAppointmentData = useCallback(async (): Promise<boolean> => {
    try {
      console.log("🚀 InitializeAppointmentData called");

      // Validate Office.js context
      if (!validateOfficeContext()) {
        console.error("❌ Office.js context not available");
        setError("Office.js context not available");
        return false;
      }

      const item = Office.context.mailbox.item;
      if (!item || item.itemType !== Office.MailboxEnums.ItemType.Appointment) {
        console.error("❌ Not an appointment item", { itemType: item?.itemType });
        setError("Not an appointment item");
        return false;
      }

      const appCompose = item as Office.AppointmentCompose;
      console.log("📋 Starting appointment data initialization...");

      // Initialize all appointment data in parallel
      const initPromises = [
        // Get appointment times
        new Promise<void>((resolve) => {
          Promise.all([
            new Promise<Date>((resolveStart) => {
              appCompose.start.getAsync((result: Office.AsyncResult<Date>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolveStart(result.value);
                } else {
                  console.warn("⚠️ Failed to get start time:", result.error);
                  resolveStart(new Date());
                }
              });
            }),
            new Promise<Date>((resolveEnd) => {
              appCompose.end.getAsync((result: Office.AsyncResult<Date>) => {
                if (result.status === Office.AsyncResultStatus.Succeeded) {
                  resolveEnd(result.value);
                } else {
                  console.warn("⚠️ Failed to get end time:", result.error);
                  resolveEnd(new Date(Date.now() + 3600000)); // Default to 1 hour later
                }
              });
            }),
          ]).then(([startTime, endTime]) => {
            console.log("📅 Setting appointment times:", { startTime, endTime });
            setAppointmentTime(startTime, endTime);
            resolve();
          });
        }),

        // Get subject
        new Promise<void>((resolve) => {
          appCompose.subject.getAsync((result: Office.AsyncResult<string>) => {
            const subject = result.status === Office.AsyncResultStatus.Succeeded ? result.value || "" : "";
            console.log("📝 Setting subject:", subject);
            setSubject(subject);
            resolve();
          });
        }),

        // Get basic location
        new Promise<void>((resolve) => {
          appCompose.location.getAsync((result: Office.AsyncResult<string>) => {
            const locationString = result.status === Office.AsyncResultStatus.Succeeded ? result.value || "" : "";
            const locationArray: OutlookLocation[] = locationString
              ? [
                  {
                    displayName: locationString,
                    emailAddress: "",
                    locationIdentifier: {
                      id: locationString,
                      type: "Custom",
                    },
                  },
                ]
              : [];
            console.log("📍 Setting location:", locationArray);
            setLocation(locationArray);
            resolve();
          });
        }),

        // Get organizer
        new Promise<void>((resolve) => {
          appCompose.organizer.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails>) => {
            const organizer =
              result.status === Office.AsyncResultStatus.Succeeded && result.value
                ? result.value.emailAddress
                : Office.context.mailbox.userProfile.emailAddress || "";
            console.log("🎯 Setting organizer:", organizer);
            setOrganizer(organizer);
            resolve();
          });
        }),

        // Get user timezone
        new Promise<void>((resolve) => {
          try {
            // Get timezone from Office context
            const userTimeZone =
              Office.context.mailbox.userProfile.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            console.log("🌍 Setting user timezone:", userTimeZone);
            setTimeZone(userTimeZone);
            resolve();
          } catch (error) {
            console.warn("⚠️ Failed to get user timezone, using browser timezone:", error);
            // Fallback to browser timezone
            const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            console.log("🌍 Setting fallback timezone:", browserTimeZone);
            setTimeZone(browserTimeZone);
            resolve();
          }
        }),

        // Get required attendees
        new Promise<void>((resolve) => {
          appCompose.requiredAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const organizerEmail = Office.context.mailbox.userProfile.emailAddress?.toLowerCase() || "";
              const attendees = (result.value || [])
                .filter((attendee) => attendee.emailAddress && attendee.emailAddress.toLowerCase() !== organizerEmail)
                .map((attendee) => ({
                  Name: attendee.displayName || attendee.emailAddress || "",
                  Email: attendee.emailAddress || "",
                  RecipientType: ExchangeResourceTypeEnum.Equipment, // Default
                }));
              console.log("👥 Setting required attendees:", attendees);
              setRequiredAttendees(attendees);
            } else {
              console.warn("⚠️ Failed to get required attendees:", result.error);
              setRequiredAttendees([]);
            }
            resolve();
          });
        }),

        // Get optional attendees
        new Promise<void>((resolve) => {
          appCompose.optionalAttendees.getAsync((result: Office.AsyncResult<Office.EmailAddressDetails[]>) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              const organizerEmail = Office.context.mailbox.userProfile.emailAddress?.toLowerCase() || "";
              const attendees = (result.value || [])
                .filter((attendee) => attendee.emailAddress && attendee.emailAddress.toLowerCase() !== organizerEmail)
                .map((attendee) => ({
                  Name: attendee.displayName || attendee.emailAddress || "",
                  Email: attendee.emailAddress || "",
                  RecipientType: ExchangeResourceTypeEnum.Equipment, // Default
                }));
              console.log("👤 Setting optional attendees:", attendees);
              setOptionalAttendees(attendees);
            } else {
              console.warn("⚠️ Failed to get optional attendees:", result.error);
              setOptionalAttendees([]);
            }
            resolve();
          });
        }),
      ];

      // Get enhanced locations if supported
      const currentPlatform = getCurrentPlatform();
      const isMailbox18Supported = !Office.context.requirements.isSetSupported("Mailbox", "1.8");
      const isPlatformMac = currentPlatform === WebAddinPlatforms.OutlookForMac;

      if (isMailbox18Supported && !isPlatformMac) {
        initPromises.push(
          new Promise<void>((resolve) => {
            appCompose.enhancedLocation.getAsync((result: Office.AsyncResult<Office.LocationDetails[]>) => {
              if (result.status === Office.AsyncResultStatus.Succeeded) {
                const locations = result.value || [];

                console.log("🏢 Setting enhanced locations:", locations);
                setEnhancedLocation(locations);
              } else {
                console.warn("⚠️ Failed to get enhanced locations:", result.error);
                setEnhancedLocation([]);
              }
              resolve();
            });
          })
        );
      }

      // Wait for all initialization to complete
      await Promise.all(initPromises);

      console.log("✅ InitializeAppointmentData completed successfully");
      setError(null); // Clear any previous errors
      return true;
    } catch (error) {
      console.error("❌ Failed to initialize appointment data:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to initialize appointment data";
      setError(errorMessage);
      return false;
    }
  }, [
    getCurrentPlatform,
    isSetSupported,
    setAppointmentTime,
    setSubject,
    setLocation,
    setEnhancedLocation,
    setOrganizer,
    setRequiredAttendees,
    setOptionalAttendees,
    setTimeZone,
    setError,
  ]);

  return {
    AddResources,
    RemoveResources,
    UpdateRecipients,
    SetLocations,
    GetRecipients,
    InitializeAppointmentData,
    ExchangeResourceTypeEnum,
  };
};
